import React from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface AddressErrors {
  postalAddress01?: string;
  postalAddress02?: string;
  postalAddress03?: string;
  postalAddress04?: string;
  postalAddress05?: string;
  deliveryAddress01?: string;
  deliveryAddress02?: string;
  deliveryAddress03?: string;
  deliveryAddress04?: string;
  deliveryAddress05?: string;
}

interface AddressSectionProps {
  postalAddress01?: string;
  postalAddress02?: string;
  postalAddress03?: string;
  postalAddress04?: string;
  postalAddress05?: string;
  deliveryAddress01?: string;
  deliveryAddress02?: string;
  deliveryAddress03?: string;
  deliveryAddress04?: string;
  deliveryAddress05?: string;
  onChange: (field: string, value: string) => void;
  disabled?: boolean;
  errors?: AddressErrors;
}

const POSTAL_FIELDS = [
  { key: 'postalAddress01', label: 'Street Address' },
  { key: 'postalAddress02', label: 'Line 2' },
  { key: 'postalAddress03', label: 'City' },
  { key: 'postalAddress04', label: 'Province / State' },
  { key: 'postalAddress05', label: 'Postal / ZIP Code' },
] as const;

const DELIVERY_FIELDS = [
  { key: 'deliveryAddress01', label: 'Street Address' },
  { key: 'deliveryAddress02', label: 'Line 2' },
  { key: 'deliveryAddress03', label: 'City' },
  { key: 'deliveryAddress04', label: 'Province / State' },
  { key: 'deliveryAddress05', label: 'Postal / ZIP Code' },
] as const;

/**
 * Reusable address section with postal (left) and delivery (right) columns.
 * Includes a "Copy Postal to Delivery" button for convenience.
 */
export const AddressSection: React.FC<AddressSectionProps> = ({
  postalAddress01 = '',
  postalAddress02 = '',
  postalAddress03 = '',
  postalAddress04 = '',
  postalAddress05 = '',
  deliveryAddress01 = '',
  deliveryAddress02 = '',
  deliveryAddress03 = '',
  deliveryAddress04 = '',
  deliveryAddress05 = '',
  onChange,
  disabled = false,
  errors,
}) => {
  const postalValues: Record<string, string> = {
    postalAddress01,
    postalAddress02,
    postalAddress03,
    postalAddress04,
    postalAddress05,
  };

  const deliveryValues: Record<string, string> = {
    deliveryAddress01,
    deliveryAddress02,
    deliveryAddress03,
    deliveryAddress04,
    deliveryAddress05,
  };

  const handleCopyPostalToDelivery = () => {
    onChange('deliveryAddress01', postalAddress01);
    onChange('deliveryAddress02', postalAddress02);
    onChange('deliveryAddress03', postalAddress03);
    onChange('deliveryAddress04', postalAddress04);
    onChange('deliveryAddress05', postalAddress05);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Addresses
        </Typography>
        <Button
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopyPostalToDelivery}
          disabled={disabled}
        >
          Copy Postal to Delivery
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Postal Address Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Postal Address
          </Typography>
          {POSTAL_FIELDS.map(({ key, label }) => (
            <TextField
              key={key}
              fullWidth
              size="small"
              label={label}
              value={postalValues[key] || ''}
              onChange={(e) => onChange(key, e.target.value)}
              disabled={disabled}
              error={!!errors?.[key as keyof AddressErrors]}
              helperText={errors?.[key as keyof AddressErrors]}
              inputProps={{ maxLength: 100 }}
              sx={{ mb: 1.5 }}
            />
          ))}
        </Grid>

        {/* Delivery Address Column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Delivery Address
          </Typography>
          {DELIVERY_FIELDS.map(({ key, label }) => (
            <TextField
              key={key}
              fullWidth
              size="small"
              label={label}
              value={deliveryValues[key] || ''}
              onChange={(e) => onChange(key, e.target.value)}
              disabled={disabled}
              error={!!errors?.[key as keyof AddressErrors]}
              helperText={errors?.[key as keyof AddressErrors]}
              inputProps={{ maxLength: 100 }}
              sx={{ mb: 1.5 }}
            />
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddressSection;
